import { useQuery } from "@apollo/client/react";
import { CheckOutlined, CreditCardOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Flex, Segmented, Space, Tag, Typography } from "antd";
import { css } from "@linaria/core";
import { type ReactNode, useState } from "react";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { UnreachableCaseError } from "ts-essentials";
import {
  GetPlanPricesDocument,
  type PlanPriceFragment,
  PlanType,
  TaxBehavior,
} from "../gql/generated/graphqlSchema.tsx";
import { formatEurWithBgn } from "../utils/priceFormat.ts";

const taxBehaviorLabel = (t: TFunction, behavior: TaxBehavior): string => {
  switch (behavior) {
    case TaxBehavior.Inclusive:
      return t("pricing.taxBehavior.inclusive");
    case TaxBehavior.Exclusive:
      return t("pricing.taxBehavior.exclusive");
    case TaxBehavior.Unspecified:
      return t("pricing.taxBehavior.unspecified");
    default:
      throw new UnreachableCaseError(behavior);
  }
};

const { Title, Paragraph, Text } = Typography;

const container = css`
  width: 100%;
  max-width: 580px;
`;

const planCard = css`
  border: 1px solid var(--ant-color-border);
`;

const planTitle = css`
  margin-bottom: var(--ant-margin-xs);
`;

const priceRow = css`
  align-items: baseline;
  gap: var(--ant-margin-xs);
`;

const monthlyEquivalent = css`
  display: block;
  margin-bottom: var(--ant-margin);
`;

const taxNote = css`
  display: block;
  font-size: var(--ant-font-size-sm);
  margin-bottom: var(--ant-margin-xs);
`;

const ribbon = css`
  font-size: var(--ant-font-size-heading-5);
`;

const featureList = css`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--ant-margin) 0;
`;

const featureItem = css`
  display: flex;
  align-items: center;
  gap: var(--ant-margin-xs);
  padding: var(--ant-padding-xs) 0;
`;

const stripeNote = css`
  display: block;
  margin-top: var(--ant-margin);
  font-size: var(--ant-font-size-sm);
  text-align: center;
`;

const DEFAULT_CTA_ICON = <CreditCardOutlined />;

type Props = {
  readonly onSelect: (plan: PlanType) => void;
  readonly loading?: boolean;
  /** Optional button label override. Defaults to "Subscribe" (billing.subscribe). */
  readonly ctaLabel?: string | undefined;
  /** Optional CTA icon override. Defaults to <CreditCardOutlined />. */
  readonly ctaIcon?: ReactNode | undefined;
  /** Optional CTA icon position. Defaults to "start". */
  readonly ctaIconPlacement?: "start" | "end" | undefined;
  /** Show the "Stripe handles your card" reassurance line below the CTA. Defaults to true. */
  readonly showStripeNote?: boolean | undefined;
};

export const PricingCards = ({
  onSelect,
  loading = false,
  ctaLabel = undefined,
  ctaIcon = DEFAULT_CTA_ICON,
  ctaIconPlacement = "start",
  showStripeNote = true,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { data } = useQuery(GetPlanPricesDocument);
  const byPlan = new Map((data?.planPrices ?? []).map((p) => [p.plan, p]));
  const monthly = byPlan.get(PlanType.Monthly);
  const annual = byPlan.get(PlanType.Annual);

  const [selectedPlan, setSelectedPlan] = useState<PlanType>(PlanType.Monthly);
  const selected = selectedPlan === PlanType.Monthly ? monthly : annual;

  const features = [
    t("pricing.feature.unlimitedRecipes"),
    t("pricing.feature.unlimitedIngredients"),
    t("pricing.feature.teamMembers"),
    t("pricing.feature.labelGeneration"),
    t("pricing.feature.support"),
  ];
  const label = ctaLabel ?? t("billing.subscribe");

  const formatInterval = (interval: string) => {
    switch (interval) {
      case "month":
        return t("pricing.intervalMonth");
      case "year":
        return t("pricing.intervalYear");
      default:
        return interval;
    }
  };

  const annualSegmentLabel =
    annual?.annualSavings != null ? (
      <strong>
        <Space size="small">
          {t("pricing.annual.name")}
          <Tag color="green" variant="solid">
            %
          </Tag>
        </Space>
      </strong>
    ) : (
      <strong>{t("pricing.annual.name")}</strong>
    );

  const description =
    selectedPlan === PlanType.Monthly ? t("pricing.monthly.description") : t("pricing.annual.description");

  const showRibbon = selectedPlan === PlanType.Annual && annual?.annualSavings != null;
  const ribbonText =
    annual?.annualSavings != null
      ? t("pricing.savingsBadge", {
          amount: formatEurWithBgn(annual.annualSavings, annual.annualSavingsBgn, i18n.language, annual.currency),
        })
      : "";

  const card = (
    <Card className={planCard}>
      <Title className={planTitle} level={2}>
        {selectedPlan === PlanType.Monthly ? t("pricing.monthly.name") : t("pricing.annual.name")}
      </Title>
      <Flex className={priceRow}>
        <Title level={2} style={{ margin: 0 }}>
          {selected ? formatEurWithBgn(selected.amount, selected.amountBgn, i18n.language, selected.currency) : "—"}
        </Title>
        <Text type="secondary">{selected ? formatInterval(selected.interval) : ""}</Text>
      </Flex>
      {selected && taxBehaviorLabel(t, selected.taxBehavior) !== "" ? (
        <Text className={taxNote} type="secondary">
          {taxBehaviorLabel(t, selected.taxBehavior)}
        </Text>
      ) : null}
      {selected?.monthlyEquivalent != null ? (
        <Text className={monthlyEquivalent} type="secondary">
          {t("pricing.annual.monthlyEquivalent", {
            amount: formatEurWithBgn(
              selected.monthlyEquivalent,
              selected.monthlyEquivalentBgn,
              i18n.language,
              selected.currency,
            ),
          })}
        </Text>
      ) : (
        <div className={monthlyEquivalent} />
      )}
      <Paragraph type="secondary">{description}</Paragraph>
      <ul className={featureList}>
        {features.map((item) => (
          <li className={featureItem} key={item}>
            <CheckOutlined /> {item}
          </li>
        ))}
      </ul>
      <Button
        block
        icon={ctaIcon}
        iconPlacement={ctaIconPlacement}
        loading={loading}
        onClick={() => onSelect(selectedPlan)}
        size="large"
        type="primary"
      >
        {label}
      </Button>
      {showStripeNote ? (
        <Text className={stripeNote} type="secondary">
          {t("pricing.stripeNote")}
        </Text>
      ) : null}
    </Card>
  );

  return (
    <Flex align="stretch" className={container} gap="middle" vertical>
      <Segmented
        block
        onChange={(value) => setSelectedPlan(value)}
        options={[
          { label: <strong>{t("pricing.monthly.name")}</strong>, value: PlanType.Monthly },
          { label: annualSegmentLabel, value: PlanType.Annual },
        ]}
        size="large"
        value={selectedPlan}
      />

      {showRibbon ? (
        <Badge.Ribbon color="green" text={<strong className={ribbon}>{ribbonText}</strong>}>
          {card}
        </Badge.Ribbon>
      ) : (
        card
      )}
    </Flex>
  );
};

// Re-exported so other modules can hold typed price objects without re-importing from codegen.
export type { PlanPriceFragment };
